import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import ScheduleModal from './ScheduleModal';
import { apiService } from '../services/api';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  professional: { id: string; name: string };
  canManage: boolean;
};

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ProfessionalWorkScheduleModal: React.FC<Props> = ({ isOpen, onClose, businessId, professional, canManage }) => {
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [actionsDropdownOpen, setActionsDropdownOpen] = useState<string | null>(null);
  const [actionsDropdownAnchor, setActionsDropdownAnchor] = useState<{ id: string; rect: DOMRect; anchorEl: HTMLElement } | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any>(null);

  const [scheduleToDelete, setScheduleToDelete] = useState<{ dayOfWeek: number; dayName: string } | null>(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);

  const hasScheduleForDay = useMemo(() => {
    const map = new Map<number, any>();
    for (const s of professionalSchedule || []) {
      map.set(s.dayOfWeek, s);
    }
    return map;
  }, [professionalSchedule]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const scheduleData = await apiService.getProfessionalSchedulesForProfessional(professional.id, businessId);
      setProfessionalSchedule(scheduleData || []);
    } catch (e) {
      console.error('Error loading professional schedule:', e);
      setError('Failed to load work schedule');
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside / scroll / resize (portal-based menu)
  useEffect(() => {
    if (!actionsDropdownOpen || !actionsDropdownAnchor) return;

    const handleGlobalPointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (actionsMenuRef.current && actionsMenuRef.current.contains(target)) return;
      if (actionsDropdownAnchor.anchorEl && actionsDropdownAnchor.anchorEl.contains(target)) return;
      setActionsDropdownOpen(null);
      setActionsDropdownAnchor(null);
    };

    const handleScrollOrResize = () => {
      setActionsDropdownOpen(null);
      setActionsDropdownAnchor(null);
    };

    document.addEventListener('mousedown', handleGlobalPointer);
    document.addEventListener('touchstart', handleGlobalPointer, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    // capture scrolls from any ancestor (including modal body)
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      document.removeEventListener('mousedown', handleGlobalPointer);
      document.removeEventListener('touchstart', handleGlobalPointer as any);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [actionsDropdownOpen, actionsDropdownAnchor]);

  useEffect(() => {
    if (!isOpen) return;
    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessId, professional.id]);

  const handleEditSchedule = (schedule: any) => {
    if (!canManage) return;
    setEditingSchedule(schedule);
    setIsScheduleModalOpen(true);
  };

  const handleSaveSchedule = async (scheduleData: any) => {
    if (!canManage) return;
    try {
      const entries: any[] = Array.isArray(scheduleData) ? scheduleData : [scheduleData];

      // Owner override endpoint: bulk upsert schedules for this professional without conflict validation.
      await apiService.ownerBulkUpsertProfessionalSchedules(businessId, professional.id, entries);

      toast.success('Schedule saved successfully');
      await loadSchedule();
    } catch (e: any) {
      console.error('Error saving professional schedule:', e);
      toast.error(e?.response?.data?.message || 'Failed to save schedule');
      throw e;
    }
  };

  const handleDeleteSchedule = async () => {
    if (!canManage) return;
    if (!scheduleToDelete) return;
    const schedule = hasScheduleForDay.get(scheduleToDelete.dayOfWeek);
    if (!schedule?.id) {
      setScheduleToDelete(null);
      return;
    }

    try {
      setIsDeletingSchedule(true);
      await apiService.ownerDeleteProfessionalScheduleDay(businessId, professional.id, scheduleToDelete.dayOfWeek);
      toast.success('Schedule deleted successfully');
      setScheduleToDelete(null);
      await loadSchedule();
    } catch (e: any) {
      console.error('Error deleting professional schedule:', e);
      toast.error(e?.response?.data?.message || 'Failed to delete schedule');
    } finally {
      setIsDeletingSchedule(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">Work Schedule</h2>
            <p className="text-sm text-gray-600 mt-1 truncate">{professional.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 overflow-auto max-h-[calc(90vh-140px)]">
          {!canManage && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm font-medium">Only Business Owners can edit another personnel’s work schedule.</p>
            </div>
          )}

          {loading ? (
            <div className="p-6">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading work schedule...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 -mx-4 md:mx-0">
              {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
                const dayName = dayNames[dayOfWeek];
                const schedule = hasScheduleForDay.get(dayOfWeek);
                const hasSchedule =
                  schedule && schedule.isAvailable && schedule.startTime && schedule.endTime;

                return (
                  <div key={dayOfWeek} className="flex items-center justify-between px-3 md:px-6 py-4 w-full">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{dayName}</h3>
                      {hasSchedule ? (
                        <p className="text-gray-700 text-sm md:text-base mt-1">
                          {schedule.startTime} - {schedule.endTime}
                        </p>
                      ) : (
                        <p className="text-gray-400 text-sm md:text-base mt-1">No schedule set</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="relative work-schedule-actions-dropdown-container">
                        <button
                          onClick={() => {
                            if (!canManage) return;
                            const nextId = `weekly-${dayOfWeek}`;
                            if (actionsDropdownOpen === nextId) {
                              setActionsDropdownOpen(null);
                              setActionsDropdownAnchor(null);
                              return;
                            }
                            const anchorEl = document.getElementById(`ws-actions-btn-${dayOfWeek}`) as HTMLElement | null;
                            const rect = anchorEl?.getBoundingClientRect?.() || new DOMRect();
                            setActionsDropdownOpen(nextId);
                            if (anchorEl) setActionsDropdownAnchor({ id: nextId, rect, anchorEl });
                          }}
                          className={`flex items-center justify-center p-2 transition-colors ${
                            canManage ? 'text-gray-400 hover:text-gray-600' : 'text-gray-200 cursor-not-allowed'
                          }`}
                          title={canManage ? 'Actions' : 'Owner-only'}
                          type="button"
                          id={`ws-actions-btn-${dayOfWeek}`}
                        >
                          <i
                            className={`bi bi-chevron-down transition-transform duration-200 text-lg ${
                              actionsDropdownOpen === `weekly-${dayOfWeek}` ? 'rotate-180' : ''
                            }`}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Nested Schedule Modal */}
        <ScheduleModal
          isOpen={isScheduleModalOpen}
          onClose={() => {
            setIsScheduleModalOpen(false);
            setEditingSchedule(null);
            loadSchedule();
          }}
          onSave={handleSaveSchedule}
          schedule={editingSchedule}
          schedules={professionalSchedule}
        />

        {/* Delete Schedule Confirmation */}
        {scheduleToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Delete Schedule</h2>
                <button
                  onClick={() => setScheduleToDelete(null)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={isDeletingSchedule}
                  type="button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete the schedule for{' '}
                  <span className="font-semibold">{scheduleToDelete.dayName}</span>? This action cannot be undone.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setScheduleToDelete(null)}
                  className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200"
                  disabled={isDeletingSchedule}
                  type="button"
                >
                  <i className="bi bi-x"></i>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSchedule}
                  disabled={isDeletingSchedule}
                  className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                >
                  {isDeletingSchedule ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Work schedule actions dropdown (portal so it won't be clipped by scroll containers/header) */}
      {canManage && actionsDropdownOpen && actionsDropdownAnchor && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={actionsMenuRef}
            className="fixed min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg py-2"
            style={(() => {
              const rect = actionsDropdownAnchor.rect;
              const estimatedMenuHeight = 140;
              const openUp = rect.bottom + estimatedMenuHeight > window.innerHeight;
              return {
                zIndex: 100000,
                left: rect.right,
                top: openUp ? rect.top - 8 : rect.bottom + 8,
                transform: openUp ? 'translate(-100%, -100%)' : 'translate(-100%, 0)'
              } as React.CSSProperties;
            })()}
          >
            {(() => {
              const id = actionsDropdownOpen;
              const match = id.match(/^weekly-(\d)$/);
              const day = match ? parseInt(match[1], 10) : null;
              if (day === null || Number.isNaN(day)) return null;

              const schedule = hasScheduleForDay.get(day);
              const hasSchedule =
                schedule && schedule.isAvailable && schedule.startTime && schedule.endTime;

              if (hasSchedule) {
                return (
                  <>
                    <button
                      onClick={() => {
                        setActionsDropdownOpen(null);
                        setActionsDropdownAnchor(null);
                        handleEditSchedule(schedule);
                      }}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      type="button"
                    >
                      <i className="bi bi-pencil text-[#0B4778]"></i>
                      Edit Schedule
                    </button>
                    <button
                      onClick={() => {
                        setActionsDropdownOpen(null);
                        setActionsDropdownAnchor(null);
                        setScheduleToDelete({ dayOfWeek: day, dayName: dayNames[day] });
                      }}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      type="button"
                    >
                      <i className="bi bi-trash text-red-600"></i>
                      Delete Schedule
                    </button>
                  </>
                );
              }

              return (
                <button
                  onClick={() => {
                    setActionsDropdownOpen(null);
                    setActionsDropdownAnchor(null);
                    handleEditSchedule({ dayOfWeek: day });
                  }}
                  className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                  type="button"
                >
                  <i className="bi bi-plus-circle text-[#0B4778]"></i>
                  Set Schedule
                </button>
              );
            })()}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProfessionalWorkScheduleModal;

